import { InfoToggle } from "./InfoToggle";
import type { Insight, InsightTone } from "./nominationInsightMath";

interface Props {
  callCheck: Insight;
  lastMonthComparison: Insight;
}

const TONE_BOX_CLASSES: Record<InsightTone, string> = {
  ok: "border-rs-green/35",
  warn: "border-amber-400/50 bg-amber-400/8",
  neutral: "border-rs-jet",
};

const TONE_ICON_CLASSES: Record<InsightTone, string> = {
  ok: "text-rs-green",
  warn: "text-amber-400",
  neutral: "text-rs-light-gray",
};

const TONE_ICONS: Record<InsightTone, string> = {
  ok: "check_circle",
  warn: "warning",
  neutral: "info",
};

function InsightBox({ insight, neutralIcon }: { insight: Insight; neutralIcon: string }) {
  return (
    <div className={`flex items-start gap-2.5 border rounded-md px-3 py-2.5 ${TONE_BOX_CLASSES[insight.tone]}`}>
      <span className={`material-symbols-sharp text-[20px] ${TONE_ICON_CLASSES[insight.tone]}`} aria-hidden="true">
        {insight.tone === "neutral" ? neutralIcon : TONE_ICONS[insight.tone]}
      </span>
      <div className="min-w-0 text-[13px]">
        <b className="block mb-0.5 font-semibold text-rs-off-white">{insight.heading}</b>
        <span className="text-rs-light-gray">{insight.detail}</span>
      </div>
    </div>
  );
}

function MathExplanation({ title, insight }: { title: string; insight: Insight }) {
  return (
    <div className="min-w-0">
      <h4 className="m-0 mb-1.5 text-[13px] font-semibold text-rs-off-white">{title}</h4>
      <ol className="m-0 pl-5 flex flex-col gap-1">
        {insight.mathSteps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </div>
  );
}

export function AnnouncementInsights({ callCheck, lastMonthComparison }: Props) {
  return (
    <div className="flex flex-col gap-2.5 mt-4">
      <div className="grid gap-2.5 md:grid-cols-2">
        <InsightBox insight={callCheck} neutralIcon="help" />
        <InsightBox insight={lastMonthComparison} neutralIcon="swap_vert" />
      </div>

      <InfoToggle label="How is this worked out?" hideLabel="Hide the math">
        <div className="grid gap-4 md:grid-cols-2">
          <MathExplanation title="Your call vs the announcements" insight={callCheck} />
          <MathExplanation title="This month vs last month" insight={lastMonthComparison} />
        </div>
        <p className="m-0">
          <b className="text-rs-green">Green</b>: your call is inside what suppliers asked for.{" "}
          <b className="text-amber-400">Amber</b>: your call is outside every ask, so double-check it before
          saving. <b className="text-rs-off-white">Grey</b>: information only, or something is missing.
        </p>
      </InfoToggle>
    </div>
  );
}
